import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarPriceDependents } from "./solar-price-dependents"
import type { SolarConcertOffersDto } from "../dto/solar-concert.dto"
import { solarProposalService } from "../services/solar-proposal.service"

/**
 * The shared picker's save variables. Solar's server fences the save on
 * today's price itself, so only the offer id goes over the wire.
 */
export type SaveSolarConcertOfferVariables = {
  proposalId: string
  body: { offerId: string; pricingContext: string }
}

/**
 * Choose a Concert offer. The server refetches Concert's offers for today's
 * price and writes the offer's Quote (no PowurOS call), so the financing read,
 * the investment section and everything that reads the Quote refetch. A
 * failure may still have stored newer offers, so it refetches them too.
 */
export default function useSaveSolarConcertOffer(): UseMutationResult<
  SolarConcertOffersDto,
  unknown,
  SaveSolarConcertOfferVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }: SaveSolarConcertOfferVariables) =>
      solarProposalService.saveConcertOffer(proposalId, {
        offerId: body.offerId,
      }),
    onSuccess: (offers, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.concertOffers(proposalId),
        offers,
      )
      invalidateSolarPriceDependents(queryClient, proposalId, {
        except: [`concertOffers`],
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.summary(proposalId),
        exact: true,
      })
    },
    onError: (_error, { proposalId }) => {
      for (const queryKey of [
        queryKeys.solarProposal.concertOffers(proposalId),
        queryKeys.solarProposal.financing(proposalId),
      ]) {
        void queryClient.invalidateQueries({ queryKey, exact: true })
      }
    },
  })
}
